import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthLeftPanel from '../components/auth/AuthLeftPanel';
import ButtonSpinner from '../components/common/ButtonSpinner';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password);

        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
            setLoading(false);
        }
    };

    return (
        <div className="w-full h-screen flex flex-col lg:flex-row relative overflow-hidden bg-navy">
            <AuthLeftPanel />
            <div className="w-full lg:w-1/2 relative flex flex-col justify-center items-center p-4 lg:p-8 h-full overflow-y-auto lg:overflow-hidden">
                {/* Mobile Background Elements */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none lg:hidden">
                    <div className="absolute top-[-10%] left-[-20%] w-[80%] h-[40%] bg-purple-500/20 rounded-full blur-[80px] animate-pulse-slow"></div>
                    <div className="absolute bottom-[-10%] right-[-20%] w-[80%] h-[40%] bg-cyan/20 rounded-full blur-[80px] animate-pulse-slow delay-1000"></div>
                </div>

                {/* Desktop Background Element */}
                <div className="hidden lg:block absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-gradient-to-br from-cyan/10 to-transparent rounded-full blur-3xl pointer-events-none opacity-40 animate-pulse-slow"></div>

                <div className="w-full max-w-sm relative z-10 flex flex-col max-h-full animate-fade-in-up">
                    {/* Mobile Logo */}
                    <div className="lg:hidden mb-8 text-center">
                        <div className="inline-flex items-center gap-2 mb-2">
                            <span className="material-symbols-outlined text-cyan text-4xl filled">bolt</span>
                            <span className="text-2xl font-bold text-white tracking-tight">OffTrigger</span>
                        </div>
                    </div>

                    <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-6 lg:p-8 border border-white/20 ring-1 ring-black/5 transition-all duration-300 hover:shadow-cyan/5">
                        <div className="mb-6 text-center">
                            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">Welcome back</h2>
                            <p className="text-sm text-gray-500">Sign in to your account</p>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border-l-4 border-pink text-pink rounded flex items-center gap-2">
                                <span className="material-symbols-outlined text-lg">error</span>
                                <p className="text-xs font-medium">{error}</p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3 mb-5">
                            <button
                                onClick={() => window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`}
                                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium transition-all hover:shadow-md text-sm group"
                            >
                                <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                                Google
                            </button>
                            <button
                                onClick={() => window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/microsoft`}
                                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-700 bg-white hover:bg-gray-50 font-medium transition-all hover:shadow-md text-sm group"
                            >
                                <svg className="w-5 h-5 group-hover:scale-110 transition-transform" viewBox="0 0 21 21">
                                    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                                    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                                    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                                    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                                </svg>
                                Microsoft
                            </button>
                        </div>

                        <div className="relative mb-5">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200"></div>
                            </div>
                            <div className="relative flex justify-center text-xs uppercase tracking-wide">
                                <span className="px-2 bg-white/95 text-gray-400 font-semibold">Or continue with</span>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide" htmlFor="email">Email</label>
                                <input
                                    className="w-full rounded-lg border-gray-300 bg-gray-50/50 text-gray-900 px-3 py-2.5 text-sm focus:ring-2 focus:ring-cyan/50 focus:border-cyan transition-all placeholder-gray-400"
                                    id="email"
                                    placeholder="name@company.com"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide" htmlFor="password">Password</label>
                                <div className="relative">
                                    <input
                                        className="w-full rounded-lg border-gray-300 bg-gray-50/50 text-gray-900 px-3 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-cyan/50 focus:border-cyan transition-all placeholder-gray-400"
                                        id="password"
                                        placeholder="••••••••"
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-cyan transition-colors"
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        <span className="material-symbols-outlined text-lg">
                                            {showPassword ? 'visibility_off' : 'visibility'}
                                        </span>
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center justify-between pt-1">
                                <div className="flex items-center">
                                    <input className="h-4 w-4 text-cyan border-gray-300 rounded focus:ring-cyan cursor-pointer" id="remember-me" name="remember-me" type="checkbox" />
                                    <label className="ml-2 block text-xs text-gray-600 cursor-pointer select-none" htmlFor="remember-me">Remember me</label>
                                </div>
                                <div className="text-xs">
                                    <a className="font-semibold text-cyan hover:text-cyan/80 transition-colors" href="#">Forgot password?</a>
                                </div>
                            </div>
                            <button
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan to-blue-500 text-white font-bold rounded-lg py-2.5 px-4 shadow-lg shadow-cyan/25 hover:shadow-cyan/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed text-sm mt-2"
                                type="submit"
                            >
                                {loading ? <ButtonSpinner size={18} className="text-white" /> : null}
                                {loading ? 'Signing in...' : 'Sign In'}
                            </button>
                        </form>
                    </div>

                    <div className="mt-6 text-center">
                        <p className="text-sm text-gray-400">
                            Don't have an account?{' '}
                            <Link className="font-bold text-cyan hover:text-white transition-colors" to="/register">Create an account</Link>
                        </p>
                    </div>

                    <div className="mt-8 text-center text-[10px] text-white/10">
                        © 2026 OffTrigger. All rights reserved.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
